import {
  EntityInfo,
  EntityInfoPathHandler,
  EntityInfoUtils
} from '@record-it-npm/synchro-common';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { DisposableContainer } from 'record-it-utils/utils/DisposableContainer/DisposableContainer';
import { EntityRepository } from '../EntityRepository/EntityRepository';
import {
  RepositoryByEntityName,
  EntityRepositoryContainer
} from '../EntityRepositoryContainer/EntityRepositoryContainer';
import { EntityRevisionStorage } from './EntityRevisionStorage';
import { EntityManagerBaseEntity } from '../EntityManagerBaseEntity';
import { EntityManagerEntityTypesByEntityName } from '../EntityManagerEntityTypesByEntityName';
import { AnyEntitiesWithoutEntityManagerSpecificFieldsWithEntityName } from '../AnyEntitiesWithoutEntityManagerSpecificFieldsWithEntityName';
import { AnyEntityWithoutEntityManagerSpecificFieldsWithEntityName } from '../AnyEntityWithoutEntityManagerSpecificFieldsWithEntityName';
import { AnySynchronizationEnvironmentTypesConstraint } from '../SynchronizationEnvironmentTypes';

export class EntityRevisionManager<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>,
  TRepositoryByEntityName extends RepositoryByEntityName<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName
  >
> {
  private static initializedRevisionManager: EntityRevisionManager<
    any,
    any,
    any
  > | null = null;

  private initialized: boolean = false;
  private entityRevisionStorage: EntityRevisionStorage<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName
  >;
  private relativeEntityInfosByEntityName: RelativeEntityInfosByEntityName<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName
  >;
  private disposableContainer: DisposableContainer = new DisposableContainer();

  constructor(
    private readonly repositoryContainer: EntityRepositoryContainer<
      TSynchronizationEnvironmentTypes,
      TEntityTypesByEntityName,
      TRepositoryByEntityName
    >
  ) {
    this.entityRevisionStorage = new EntityRevisionStorage<
      TSynchronizationEnvironmentTypes,
      TEntityTypesByEntityName
    >(
      repositoryContainer
        .getAll()
        .map((repository) => repository.getEntityInfo()),
      this.getRevisionInfoStorageKeyForEntityName.bind(this)
    );

    this.relativeEntityInfosByEntityName =
      this.createRelativeEntityInfosByEntityName(repositoryContainer);
  }

  public async init(): Promise<void> {
    if (this.initialized) {
      return;
    }

    await this.entityRevisionStorage.init();
    this.initHooks();

    if (EntityRevisionManager.initializedRevisionManager) {
      throw new Error(
        'only one EntityRevisionManager is allowed to be initialized'
      );
    }
    EntityRevisionManager.initializedRevisionManager = this;

    this.initialized = true;
  }

  public destroy(): void {
    this.entityRevisionStorage.destroy();

    this.disposableContainer.disposeAll();
    EntityRevisionManager.initializedRevisionManager = null;
  }

  public processActualizedEntities(
    entitiesWithEntityName: AnyEntitiesWithoutEntityManagerSpecificFieldsWithEntityName<
      TSynchronizationEnvironmentTypes,
      TEntityTypesByEntityName
    >
  ): void {
    this.assertInitialized();

    const revisionsByEntityName: RevisionsByEntityName<TSynchronizationEnvironmentTypes> =
      new Map();
    let highestRevision = 0;

    for (const entity of entitiesWithEntityName.entities) {
      highestRevision = Math.max(highestRevision, entity.revision);
      this.processRelativeRevisions(
        {
          entityName: entitiesWithEntityName.entityName,
          entity
        },
        revisionsByEntityName
      );
    }

    this.entityRevisionStorage.updateRevision(
      entitiesWithEntityName.entityName,
      highestRevision
    );
    this.setRelativeRevisions(
      entitiesWithEntityName.entityName,
      revisionsByEntityName
    );
  }

  public getRevision(
    entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']
  ): number {
    this.assertInitialized();

    return this.entityRevisionStorage.getRevision(entityName);
  }

  public getRelativeRevision(
    entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
    relativeEntityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
    relativeEntityId: EntityManagerBaseEntity['id']
  ): number {
    this.assertInitialized();

    return this.entityRevisionStorage.getRelativeRevision(
      entityName,
      relativeEntityName,
      relativeEntityId
    );
  }

  public resetRelativeRevision(
    relativeEntityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
    relativeEntityId: EntityManagerBaseEntity['id']
  ): void {
    this.entityRevisionStorage.resetRelativeRevision(
      relativeEntityName,
      relativeEntityId
    );
  }

  public async flush(): Promise<void> {
    await this.entityRevisionStorage.flush();
  }

  private createRelativeEntityInfosByEntityName(
    repositoryContainer: EntityRepositoryContainer<
      TSynchronizationEnvironmentTypes,
      TEntityTypesByEntityName,
      TRepositoryByEntityName
    >
  ): RelativeEntityInfosByEntityName<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName
  > {
    const map: RelativeEntityInfosByEntityName<
      TSynchronizationEnvironmentTypes,
      TEntityTypesByEntityName
    > = {};

    const repositories = repositoryContainer.getAll() as unknown as Array<
      EntityRepository<
        TSynchronizationEnvironmentTypes,
        TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
        TEntityTypesByEntityName
      >
    >;

    for (const repository of repositories) {
      const info = repository.getEntityInfo() as unknown as EntityInfo<
        TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'],
        TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
        TEntityTypesByEntityName[TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']]['entity']
      >;

      map[info.entityName] = this.getRelativeEntityInfos(info);
    }

    return map;
  }

  private getRelativeEntityInfos<
    TEntityName extends
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']
  >(
    entityInfo: EntityInfo<
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'],
      TEntityName,
      TEntityTypesByEntityName[TEntityName]['entity']
    >
  ): Array<
    RelativeEntityInfo<
      TSynchronizationEnvironmentTypes,
      TEntityName,
      TEntityTypesByEntityName
    >
  > {
    const relativeSynchronizationFieldInfos =
      EntityInfoUtils.getAllRelativeSynchronizationFieldInfos<
        TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'],
        TEntityName,
        any
      >(entityInfo);

    return entityInfo.synchronizeRelativeTo.map((relativeEntityName) => {
      if (relativeEntityName === entityInfo.entityName) {
        return {
          pathHandler: new EntityInfoPathHandler({ entityInfo, path: ['id'] }),
          referencedEntityName: relativeEntityName
        };
      }

      const fieldInfo = relativeSynchronizationFieldInfos.find(
        (info) => info.ref === relativeEntityName
      );
      assertNotNullOrUndefined(
        fieldInfo,
        `${entityInfo.entityName} no relativeSynchronization field info found for ${relativeEntityName}`
      );

      return {
        pathHandler: new EntityInfoPathHandler({
          entityInfo,
          path: fieldInfo.path
        }),
        referencedEntityName: relativeEntityName
      };
    });
  }

  /**
   *
   * @param entityName
   * @param entity
   * @param revisionsByEntityName - gets modified in place
   */
  private processRelativeRevisions(
    entityWithEntityName: AnyEntityWithoutEntityManagerSpecificFieldsWithEntityName<
      TSynchronizationEnvironmentTypes,
      TEntityTypesByEntityName
    >,
    revisionsByEntityName: RevisionsByEntityName<TSynchronizationEnvironmentTypes>
  ): void {
    const relativeInfos =
      this.relativeEntityInfosByEntityName[entityWithEntityName.entityName] ??
      [];

    for (const info of relativeInfos) {
      let revisionById = revisionsByEntityName.get(info.referencedEntityName);
      if (!revisionById) {
        revisionById = new Map();
        revisionsByEntityName.set(info.referencedEntityName, revisionById);
      }

      const id = info.pathHandler.firstValue({
        data: entityWithEntityName.entity
      });
      // this is just plain wrong, id can be an empty string for example, so we need the check
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (id && typeof id === 'string') {
        const currentRevision = revisionById.get(id) ?? 0;
        revisionById.set(
          id,
          Math.max(currentRevision, entityWithEntityName.entity.revision)
        );
      }
    }
  }

  private setRelativeRevisions(
    entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
    revisionsByEntityName: RevisionsByEntityName<TSynchronizationEnvironmentTypes>
  ): void {
    for (const [
      relativeEntityName,
      revisionsById
    ] of revisionsByEntityName.entries()) {
      for (const [relativeEntityId, revision] of revisionsById.entries()) {
        this.entityRevisionStorage.updateRelativeRevision(
          entityName,
          relativeEntityName,
          relativeEntityId,
          revision
        );
      }
    }
  }

  private getRevisionInfoStorageKeyForEntityName(
    entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']
  ): string {
    return `EntityRevisionManager::revisionInfo_${entityName}`;
  }

  private initHooks(): void {
    const repositories = this.repositoryContainer.getAll();
    for (const repository of repositories) {
      const hookCallback = (
        entity: TEntityTypesByEntityName[TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']]['entity']
      ): void => {
        const entityName = repository.getEntityInfo()
          .entityName as TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'];
        this.entityRevisionStorage.resetRelativeRevision(entityName, entity.id);
      };

      this.disposableContainer.add(
        repository.registerHooks({
          afterEntityDeleted: hookCallback,
          afterEntityRemovedLocally: hookCallback
        })
      );
    }
  }

  private assertInitialized(): void {
    if (!this.initialized) {
      throw new Error(
        "can't use the EntityRevisionManager because it isn't initialized"
      );
    }
  }
}

type RelativeEntityInfosByEntityName<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>
> = Partial<{
  [TEntityName in TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']]: Array<
    RelativeEntityInfo<
      TSynchronizationEnvironmentTypes,
      TEntityName,
      TEntityTypesByEntityName
    >
  >;
}>;

type RelativeEntityInfo<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityName extends
    TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>
> = {
  referencedEntityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'];
  pathHandler: EntityInfoPathHandler<
    TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'],
    TEntityTypesByEntityName[TEntityName]['entity']
  >;
};

type RevisionsByEntityName<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint
> = Map<
  TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
  RevisionById
>;

type RevisionById = Map<EntityManagerBaseEntity['id'], number>;
